import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import AnimatedNumber from 'react-animated-number';
import { makeStyles } from 'tss-react/mui';
import { StickyWrapper, StickyWrapperProps } from '../stickyPanel';

export type EmobilityResultStickyWrapperProps<E extends HTMLElement> =
	Omit<StickyWrapperProps<E>, 'stickyPanelContent'> &
	{ contentProps: EmobilityResultStickyContentProps };

export const EmobilityResultStickyWrapper = <E extends HTMLElement>(props: EmobilityResultStickyWrapperProps<E>) => {
	const theme = useTheme();
	const desktop = useMediaQuery(theme.originalBreakpoints.up('md'));

	if (desktop) {
		const { contentProps, classes, detailElement, loading, color, mode, hideAfterDetail, ...other } = props;
		return <div {...other} />;
	} else {
		const { contentProps, ...other } = props;
		return <StickyWrapper {...other} stickyPanelContent={<EmobilityResultStickyContent {...contentProps} />} />;
	}
};

EmobilityResultStickyWrapper.defaultProps = {
	hideAfterDetail: true
};

const useStyles = makeStyles({ name: 'EmobilityResultSticky' })(theme => ({
	root: {
		display: 'flex',
		justifyContent: 'space-between',
		margin: -10
	},
	item: {
		padding: 10,
		display: 'flex',
		flexDirection: 'column',
		'&.left': {

		},
		'&.right': {
			textAlign: 'end'
		}
	},
	label: {
		fontSize: '12px',
		lineHeight: '16px'
	},
	model: {
		fontSize: '16px',
		lineHeight: '24px'
	},
	result: {
		fontSize: '20px',
		lineHeight: '24px',
		fontWeight: 'bold'
	}
}));

type EmobilityResultStickyContentClasses = Partial<ReturnType<typeof useStyles>['classes']>;

type EmobilityResultStickyContentProps = {
	classes?: EmobilityResultStickyContentClasses;
	labelModel: string;
	labelResult: string;
	model: string;
	result: number;
	formatResult?: (value: number) => string;
};

const EmobilityResultStickyContent: React.FC<EmobilityResultStickyContentProps> = (props) => {
	const { labelModel, labelResult, model, result, formatResult } = props;
	const { classes: styles, cx } = useStyles(undefined, { props });

	return (
		<div className={styles.root} data-test='result-sticky-content'>
			<div className={cx(styles.item, 'left')}>
				<span className={styles.label}>{labelModel}</span>
				<span className={styles.model}>{model}</span>
			</div>
			<div className={cx(styles.item, 'right')}>
				<span className={styles.label}>{labelResult}</span>
				<AnimatedNumber
					className={styles.result}
					value={result}
					duration={300}
					formatValue={formatResult ? formatResult : undefined} />
			</div>
		</div>
	);
};
